import { buildModel, type DateKey, decodeOutcomes, formatDuration, levelName, type PuzzleModel } from '@arrow/game-engine';
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
	ApiError,
	type Challenge,
	type DailyPuzzle,
	fetchChallenge,
	fetchDailyByNumber,
	fetchPercentile,
	type LoadError,
} from '../api/client.ts';
import { cacheDaily, cachedDaily, setHandoff } from '../app/handoff.ts';
import { setPageTitle } from '../app/page-title.ts';
import { goUp, navigate } from '../app/router.tsx';
import { AppMenu } from '../components/AppMenu.tsx';
import { BackPill } from '../components/BackPill.tsx';
import { routeStyle } from '../components/BoardMap.tsx';
import { BoardMap } from '../components/BoardMap.tsx';
import { Swords, Wedge } from '../components/icons.tsx';
import { Nib } from '../components/Nib.tsx';
import { ErrorState, Pending, Skeleton, StateBody, StatePage } from '../components/States.tsx';
import type { ChallengeContext } from '../game/challenge.ts';
import { weekdayName } from '../game/view.ts';
import { track } from '../platform/analytics.ts';
import { myChallenges, setPlayingChallenge } from '../platform/challenges.ts';
import { structureModel } from '../app/structure.ts';
import { type Persistence, slotFor } from '../storage/persistence.ts';

interface ChallengeScreenProps {
	code: string;
	todayKey: DateKey;
	persistence: Persistence;
	dev?: { message: boolean } | undefined;
}

type Load =
	| { status: 'loading' }
	| { status: 'error'; error: LoadError }
	| { status: 'ready'; challenge: Challenge; daily: DailyPuzzle | null };

/** What a challenge game carries: the challenge, minus what the game doesn't need. */
export function contextOf(c: Challenge): ChallengeContext {
	return { code: c.code, number: c.number, level: c.level, date: c.date, durationMs: c.durationMs, paceMs: c.paceMs };
}

/**
 * The challenge's board, letter-free: its structure, and the challenger's Flow as cobalt
 * bars (every answer whose cells all fell in a Flow). Never a letter or a clue: the
 * model is `structureModel`'s, and the route is answer ids, not text.
 */
export function challengeBoard(c: Challenge, daily: DailyPuzzle | null): { model: PuzzleModel; chain: string[] } | null {
	if (!daily) return null;
	const full = buildModel(daily.puzzle);
	if (full.width !== c.width || full.height !== c.height) return null;
	const model = structureModel(full);
	const outcomes = c.cells ? decodeOutcomes(c.cells, c.width, c.height) : null;
	const chain = outcomes
		? model.answerOrder.filter((id) => {
				const cells = model.answers[id]?.cells ?? [];
				return cells.length > 0 && cells.every((cell) => outcomes[cell] === 'flow');
			})
		: [];
	return { model, chain };
}

/**
 * `/c/:code`: "You've been challenged". The time to beat, huge; the board the challenger
 * solved, letter-free, with their Flow in cobalt; chips for Flow, checks and (once it's
 * honest) the percentile; then Take the challenge, which plays that daily (as an archive
 * day if it isn't today's) racing the challenger's pace. Someone who already solved it
 * goes straight to the comparison on their poster. Anonymous: no names anywhere.
 */
export function ChallengeScreen({ code, todayKey, persistence, dev }: ChallengeScreenProps) {
	const [load, setLoad] = useState<Load>({ status: 'loading' });
	const [attempt, setAttempt] = useState(0);
	const [percentile, setPercentile] = useState<number | null>(null);

	useEffect(() => {
		const ctrl = new AbortController();
		setLoad({ status: 'loading' });
		(async () => {
			const challenge = await fetchChallenge(code, ctrl.signal);
			let daily: DailyPuzzle | null = cachedDaily({ number: challenge.number }, challenge.level) ?? null;
			if (!daily) {
				try {
					daily = await fetchDailyByNumber(challenge.number, challenge.level, ctrl.signal);
					cacheDaily(daily);
				} catch (err) {
					if (err instanceof DOMException && err.name === 'AbortError') throw err;
					daily = null; // The landing still works on the time alone.
				}
			}
			return { status: 'ready', challenge, daily } as const;
		})().then(setLoad, (err: unknown) => {
			if (ctrl.signal.aborted) return;
			setLoad({
				status: 'error',
				error: err instanceof ApiError ? err.detail : { kind: 'network', message: 'Something went wrong loading the challenge.' },
			});
		});
		return () => ctrl.abort();
	}, [code, attempt]);

	const ready = load.status === 'ready' ? load : null;
	const missing = load.status === 'error' && load.error.kind === 'not-found';
	useEffect(() => {
		if (missing) setPageTitle('That challenge link doesn’t work');
	}, [missing]);

	// Opened: track it once, and send someone who already solved it to the comparison.
	const opened = useRef<string | null>(null);
	const [redirecting, setRedirecting] = useState(false);
	useEffect(() => {
		if (!ready || opened.current === ready.challenge.code) return;
		opened.current = ready.challenge.code;
		const c = ready.challenge;
		const slot = slotFor({ kind: 'daily', date: c.date, level: c.level });
		const solved = persistence.resumeInfo(slot).status === 'completed';
		const mine = myChallenges().some((m) => m.code === c.code);
		setPageTitle('You’ve been challenged');
		track('challenge_opened', { code: c.code, number: c.number, level: c.level, alreadySolved: solved });
		void fetchPercentile(c.number, c.level, c.durationMs).then((s) => setPercentile(s?.percentile ?? null));
		if (solved && ready.daily) {
			// Already solved here: the comparison, settled (no rematch to send, QA2-2).
			if (!mine) setPlayingChallenge(slot, { ...contextOf(c), settled: true });
			setHandoff({ slot, rect: null, ghost: null, openResult: true, at: Date.now() });
			setRedirecting(true);
			navigate(`/daily/${c.number}?l=${c.level}`, { replace: true });
		}
	}, [ready, persistence]);

	const menu = <AppMenu screen="challenge" install={{ todayKey }} />;
	if (load.status === 'loading' || redirecting) return <ChallengeLoading menu={menu} />;
	if (missing)
		return (
			<StatePage back="/" screen="challenge" testId="challenge-missing">
				<StateBody line="That challenge link doesn’t work." sub="It may have been mistyped, or only part of it was copied.">
					<div className="state-actions">
						<button type="button" className="btn lg primary fit" onClick={() => goUp('/')}>
							Play today’s Sparrow
							<Wedge direction="right" />
						</button>
					</div>
				</StateBody>
			</StatePage>
		);
	if (load.status === 'error')
		return (
			<ErrorState
				error={load.error}
				what="this challenge"
				onRetry={() => setAttempt((n) => n + 1)}
				link={{ label: 'Play today’s Sparrow', onClick: () => goUp('/') }}
				back="/"
				screen="challenge"
				dev={dev}
			/>
		);
	return <ChallengeLanding challenge={load.challenge} daily={load.daily} todayKey={todayKey} percentile={percentile} menu={menu} />;
}

/**
 * While the challenge loads: the landing's own masthead at once ("‹ Today", ≡), so nothing
 * jumps when it arrives, and, if the wait is noticeable, the card and the board as quiet
 * placeholders (UX-015).
 */
function ChallengeLoading({ menu }: { menu: ReactNode }) {
	return (
		<div className="page challenge landing" data-testid="challenge-loading">
			<main className="page-body" aria-busy="true">
				<header className="page-mast ch-mast">
					<BackPill to="/" />
					{menu}
				</header>
				<Pending label="Loading the challenge">
					<Skeleton className="ch-skel-card" />
					<Skeleton className="ch-skel-map" />
				</Pending>
			</main>
		</div>
	);
}

interface LandingProps {
	challenge: Challenge;
	daily: DailyPuzzle | null;
	todayKey: DateKey;
	percentile: number | null;
	/** ≡, top right. */
	menu?: ReactNode;
}

export function ChallengeLanding({ challenge: c, daily, todayKey, percentile, menu }: LandingProps) {
	const board = useMemo(() => challengeBoard(c, daily), [c, daily]);
	const time = formatDuration(c.durationMs);
	const lv = levelName(c.level);
	const isToday = c.date === todayKey;
	const on = isToday ? `today’s ${lv}` : `${weekdayName(c.date)}’s ${lv}, No. ${c.number}`;
	const pct = typeof percentile === 'number' && percentile >= 1 ? Math.floor(Math.min(100, percentile)) : null;
	const help = [c.checks ? `${c.checks} ${c.checks === 1 ? 'check' : 'checks'}` : null, c.reveals ? `${c.reveals} ${c.reveals === 1 ? 'reveal' : 'reveals'}` : null].filter(
		(x): x is string => x !== null,
	);
	const as = board ? (board.chain.length > 1 ? (routeStyle(board.chain.length, board.model.answerOrder.length) === 'whole' ? 'whole' : 'bars') : 'line') : 'line';

	const take = () => {
		const slot = slotFor({ kind: 'daily', date: c.date, level: c.level });
		setPlayingChallenge(slot, contextOf(c));
		if (daily) cacheDaily(daily);
		// The landing is an interstitial (UX-050): the game takes its place, so back goes to Today.
		navigate(`/daily/${c.number}?l=${c.level}`, { replace: true });
	};

	return (
		<div className="page challenge landing" data-testid="challenge-landing">
			<main className="page-body page-scroll">
				{/* "‹ Today" top left (a newcomer learns there's a Today), ≡ top right. */}
				<header className="page-mast ch-mast">
					<BackPill to="/" />
					{menu}
				</header>

				<section className="dare-card" aria-labelledby="dare-who">
					<h1 id="dare-who" className="dare-who">
						You’ve been challenged
						{/* Nib, cheeky: "Beat me?" The landing's one illustration. */}
						<Nib pose="cheeky" className="dare-nib" type={daily?.puzzle.type} />
					</h1>
					<div className="dare-row">
						<div className="dare-time-box">
							<p className="dare-time" data-len={Math.min(7, time.length)}>
								<span className="sr-only">Time to beat: </span>
								{time}
							</p>
							<p className="dare-sub">to beat on {on}</p>
						</div>
					</div>
					{/* Facts about their solve, not buttons: info chips (UX-010). */}
					<ul className="chips" aria-label="How it went">
						{c.bestFlow >= 2 && <li className="chip info sm flow">Flow ×{c.bestFlow}</li>}
						{help.length ? (
							help.map((h) => (
								<li key={h} className="chip info sm">
									{h}
								</li>
							))
						) : (
							<li className="chip info sm">Perfect solve</li>
						)}
						{pct !== null && <li className="chip info sm">Faster than {pct}% of solvers</li>}
					</ul>
				</section>

				{/* The board they solved, bare, with their Flow: the landing's picture, taking the
				    height there is (as Today's hero and the poster's map do). */}
				{board && (
					<div
						className="ch-map"
						style={{ '--map-ar': board.model.width / board.model.height, '--map-cols': board.model.width } as CSSProperties}
					>
						<BoardMap
							model={board.model}
							chain={board.chain}
							routeAs={as}
							label={
								board.chain.length > 1
									? 'The board your challenger solved, without letters, with their longest Flow marked'
									: 'The board your challenger solved, without letters'
							}
						/>
					</div>
				)}

				<p className="ch-body">Sparrow is a daily Arrowword: the clues sit in the grid, and arrows show where each answer goes.</p>

				<button type="button" className="btn lg dare ch-take" onClick={take}>
					<Swords />
					Take the challenge
				</button>
				<p className="ch-fine">Free, no sign-up.</p>
			</main>
		</div>
	);
}
